import type { BrandFontItem } from '../api/brandFont.api';

/**
 * Signed urls by asset id, typed by shape rather than imported from the asset entity, which is a
 * sibling this one may not reach for. Same reason `media-tile.ts` declares its own.
 */
type ResolvedUrls = Readonly<Record<string, string>>;

/** One font, flattened to what a specimen card needs. */
export interface FontSpecimen {
  id: string;
  /** What to call it. The filename stands in when the family never came through. */
  label: string;
  /**
   * The font file. Undefined until the signed url resolves, and a card with no url draws its sample
   * in the interface font rather than nothing, so the row still reads as a font that exists.
   */
  url?: string;
  weight?: string;
  style?: string;
}

/** Every asset id a set of fonts needs a signed url for. */
export const fontUrlIds = (fonts: readonly BrandFontItem[]): string[] =>
  fonts.map((font) => font.assetId);

export const toFontSpecimen = (
  font: BrandFontItem,
  urls: ResolvedUrls | undefined,
): FontSpecimen => ({
  id: font.id,
  label: font.familyName ?? font.filename,
  url: urls?.[font.assetId],
  ...(font.weight !== null ? { weight: font.weight } : {}),
  ...(font.style !== null ? { style: font.style } : {}),
});

/**
 * The `font-family` a specimen's sample is drawn in.
 *
 * Keyed by the row id rather than the family name: two uploads can legitimately claim the same
 * family (a regular and a bold shipped as separate files), and letting them share a face would make
 * whichever loaded last win for both.
 */
export const specimenFamily = (id: string): string => `brand-font-specimen-${id}`;
